import React from 'react';
import { SelectOption } from 'global';
import { useQuery } from '@apollo/client';
import { AccountType } from '@trnsact/trnsact-shared-types';
import { Grid, Stack, CircularProgress } from '@mui/material';
import { CardContainer } from 'components/shared/CardContainer/CardContainer';
import { RenderWithFallback } from 'components/shared/Conditional/RenderWithFallback';
import {
  useExternalDashboards,
  adminExternalDashboardsApi,
  AdminDashboardQueryResponse,
  AdminDashboardOptionsListResponse,
} from 'modules/integrations/externalDashboards';

import { PortalConfigurationRenderProps } from '../../types';
import { ExternalDashboardForm } from './ExternalDashboardForm';
import { AccountId, DashboardId, ExternalDashboardsPortalConfigFormValues } from '../types';

export const ExternalDashboardManagement = (props: PortalConfigurationRenderProps) => {
  const { handleUpdateAccessByAccount, isUpdatingAccessByAccount } = useExternalDashboards();

  const { data: dashboardsResponse, loading: isDashboardListOptionsLoading } =
    useQuery<AdminDashboardOptionsListResponse>(adminExternalDashboardsApi.getAdminDashboardListOptions, {
      variables: { input: {} },
    });

  const { data: dashBoardAccessByAccount, loading: isDashBoardAccessByAccountLoading } =
    useQuery<AdminDashboardQueryResponse>(adminExternalDashboardsApi.getDashboardAccountAccess);

  const handleSave = async (formData: ExternalDashboardsPortalConfigFormValues) => {
    const dto = formData.dashboards.map(configItem => ({
      ...configItem,
      accounts: configItem.accounts.map(({ value }) => value),
      accountTypes: Object.entries(configItem.accountTypes).reduce<string[]>((acc, [key, value]) => {
        if (value) acc.push(key);
        return acc;
      }, []),
    }));

    await handleUpdateAccessByAccount(dto);
  };

  const isLoading = isDashboardListOptionsLoading || isDashBoardAccessByAccountLoading;

  // Only process data when both queries have completed
  const externalDashboardOptions: SelectOption[] = !isLoading
    ? (dashboardsResponse?.externalDashboards?.result?.list ?? [])
    : [];

  const accessByAccountType = !isLoading
    ? (dashBoardAccessByAccount?.externalDashboards?.result?.entity?.byAccountType ?? []).reduce<
        Record<string, string[]>
      >((acc, { accountTypes, dashboardId }) => {
        acc[dashboardId] = accountTypes;
        return acc;
      }, {})
    : {};

  const initialAcc = !isLoading
    ? externalDashboardOptions.reduce<
        Record<DashboardId, { accountTypes: string[]; accounts: SelectOption<AccountId>[] }>
      >((initialAcc, { value }) => {
        initialAcc[value] = { accounts: [], accountTypes: accessByAccountType[value] ?? [] };
        return initialAcc;
      }, {})
    : {};

  const groupedAccountsByDashboard = !isLoading
    ? (dashBoardAccessByAccount?.externalDashboards?.result?.entity?.byAccount ?? []).reduce<
        Record<DashboardId, { accountTypes: string[]; accounts: SelectOption<AccountId>[] }>
      >((acc, item) => {
        if (acc[item.dashboardId]?.accounts && Array.isArray(acc[item.dashboardId].accounts)) {
          acc[item.dashboardId].accounts.push({ value: item.accountId, label: item.accountName });
        }
        return acc;
      }, initialAcc)
    : {};

  const initialValue = !isLoading
    ? Object.entries(groupedAccountsByDashboard).reduce<ExternalDashboardsPortalConfigFormValues>(
        (acc, [dashboardId, { accountTypes, accounts }]) => {
          acc.dashboards.push({
            accounts,
            dashboardId,
            accountTypes: accountTypes.reduce<Record<string, boolean>>(
              (acc, accountType) => {
                acc[accountType] = true;
                return acc;
              },
              { [AccountType.Dealer]: false, [AccountType.Lender]: false, PARTNER: false }
            ),
          });
          return acc;
        },
        { dashboards: [] }
      )
    : { dashboards: [] };

  return (
    <Grid item sm={12} md={6}>
      <CardContainer title="External Dashboards Accounts Access Settings">
        <RenderWithFallback
          isLoading={isLoading}
          fallBack={
            <Stack alignItems="center" justifyContent="center" height="100%">
              <CircularProgress />
            </Stack>
          }
        >
          <ExternalDashboardForm
            onConfirm={handleSave}
            initialValue={initialValue}
            isLoading={isUpdatingAccessByAccount}
            externalDashboardOptions={externalDashboardOptions}
          />
        </RenderWithFallback>
      </CardContainer>
    </Grid>
  );
};
